/** @jsxImportSource @emotion/react */

import {
  CSSRulesResolver,
  useCSSRulesWithTheme,
} from "hooks/useCSSRulesWithTheme";
import { MissionSpecCard } from "models/MissionSpec";
import { FC } from "react";
import { countOpenRoles, humanReadableStatus } from "views/HomeV2/utils";
import actionIconPurple from "views/HomeV2/assets/quick-action-purple.svg";
import diamondIconWhite from "views/HomeV2/assets/diamond-white.svg";
import InputDivider from "views/Mission/TeamSpecV2/components/InputDivider";
import { useHistory } from "react-router-dom";

const getCSSRules: CSSRulesResolver<{ purpleBG?: boolean }> = ({
  colors,
  purpleBG,
}) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    backgroundColor: colors.Grey[100],
    color: colors.Grey[500],
    fontSize: 14,
    lineHeight: "20px",
    fontWeight: 500,
    height: 40,
    padding: "0 12px",
    borderRadius: 16,

    ...(purpleBG && {
      backgroundColor: colors.Hannibal[600],
      color: "white",
    }),
  },
  iconAndText: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    whiteSpace: "nowrap",
  },
  inputDivider: {
    height: 12,
    opacity: 0.3,
  },
  icon: {
    width: 16,
    height: 16,
    color: "white",
  },
});

const ActionIcon: FC<{ type: "diamond" | "action" | null }> = ({ type }) => {
  const styles = useCSSRulesWithTheme(getCSSRules);
  if (type === null) {
    return null;
  }
  const iconSrc = type === "diamond" ? diamondIconWhite : actionIconPurple;
  return <img css={styles.icon} src={iconSrc} alt="Action icon" />;
};

const StatusButton: FC<{
  href?: string;
  mission?: MissionSpecCard;
  linkToProposals?: boolean;
  startAProject?: boolean;
}> = ({ mission, linkToProposals, startAProject, href }) => {
  const history = useHistory();
  const { status, platformStatus } = mission || {};
  const openRolesCount = countOpenRoles(mission?.roles);
  const humanStatus = humanReadableStatus(
    status,
    platformStatus,
    linkToProposals,
    openRolesCount,
    startAProject
  );

  const iconType = linkToProposals
    ? "diamond"
    : humanStatus?.match(/open role/)
    ? "action"
    : null;

  const purpleBG = linkToProposals || startAProject;
  const styles = useCSSRulesWithTheme(getCSSRules, { purpleBG });

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (href) {
      e.stopPropagation();
      history.push(href);
    }
  };

  if (!startAProject && (!mission || !humanStatus)) {
    return null;
  }

  return (
    <div css={styles.container} onClick={handleClick}>
      <div css={styles.iconAndText}>
        <ActionIcon type={iconType} />
        <span>{humanStatus}</span>
      </div>

      {linkToProposals && (
        <>
          <InputDivider css={styles.inputDivider} />
          Open
        </>
      )}
    </div>
  );
};

export default StatusButton;
